<script setup lang="ts">
import { useDisplay } from 'vuetify';
import { useAuthStore } from '~/stores/use-auth-store';

const authStore = useAuthStore();
const { mobile } = useDisplay();
const drawer = ref(!mobile.value);

const navItems = [
  { title: 'Dashboard', icon: 'mdi-view-dashboard', to: '/dashboard' },
  { title: 'My Tickets', icon: 'mdi-ticket', to: '/dashboard/tickets' },
  { title: 'Saved Events', icon: 'mdi-heart-outline', to: '/dashboard/saved' },
  {
    title: 'My Submissions',
    icon: 'mdi-file-document-multiple-outline',
    to: '/dashboard/submissions',
  },
  {
    title: 'My Ideas',
    icon: 'mdi-lightbulb-on-outline',
    to: '/dashboard/ideas',
  },
  {
    title: 'Payment Methods',
    icon: 'mdi-credit-card',
    to: '/dashboard/payment-methods',
  },
  { title: 'Payouts', icon: 'mdi-cash-multiple', to: '/dashboard/stripe' },
  { title: 'Create Event', icon: 'mdi-plus-circle', to: '/events/create' },
  { title: 'Browse Events', icon: 'mdi-calendar-search', to: '/browse' },
];

// Mobile bar stays at five items to avoid crowding — the "My Submissions"
// surface is reachable from the (drawer) nav and the dashboard actions.
const bottomNavItems = [
  { title: 'Dashboard', icon: 'mdi-view-dashboard', to: '/dashboard' },
  { title: 'My Tickets', icon: 'mdi-ticket', to: '/dashboard/tickets' },
  { title: 'Payouts', icon: 'mdi-cash-multiple', to: '/dashboard/stripe' },
  { title: 'Create', icon: 'mdi-plus-circle', to: '/events/create' },
  { title: 'Browse', icon: 'mdi-calendar-search', to: '/browse' },
];

function handleLogout() {
  authStore.logout();
  navigateTo('/browse');
}
</script>

<template>
  <v-app>
    <v-app-bar flat color="surface" border="b">
      <v-app-bar-nav-icon class="d-md-none" @click="drawer = !drawer" />
      <v-app-bar-title>
        <NuxtLink
          to="/"
          class="text-decoration-none text-on-surface d-inline-flex align-center"
          aria-label="Arvee Events home"
        >
          <CommonAppLogo />
          <CommonBetaBadge />
        </NuxtLink>
      </v-app-bar-title>

      <template #append>
        <v-menu v-if="authStore.user">
          <template #activator="{ props }">
            <v-btn icon v-bind="props">
              <CommonUserAvatar :user="authStore.user" :size="32" />
            </v-btn>
          </template>

          <v-list min-width="200">
            <v-list-item>
              <v-list-item-title class="font-weight-medium">
                {{ userDisplayName(authStore.user) }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ authStore.user.email }}
              </v-list-item-subtitle>
            </v-list-item>
            <v-divider />
            <v-list-item prepend-icon="mdi-logout" @click="handleLogout">
              Log out
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
    </v-app-bar>

    <v-navigation-drawer v-model="drawer" :temporary="mobile">
      <v-list nav>
        <v-list-item
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          :prepend-icon="item.icon"
          :title="item.title"
          color="primary"
        />
      </v-list>
    </v-navigation-drawer>

    <v-main>
      <ClientOnly><ConnectivityBanner /></ClientOnly>
      <v-container>
        <slot />
      </v-container>
    </v-main>

    <v-bottom-navigation class="d-md-none" grow>
      <v-btn v-for="item in bottomNavItems" :key="item.to" :to="item.to">
        <v-icon>{{ item.icon }}</v-icon>
        <span class="text-caption">{{ item.title }}</span>
      </v-btn>
    </v-bottom-navigation>
  </v-app>
</template>
